import React, { useCallback, useEffect, useMemo, useRef, Suspense, useState } from 'react';
import { TabProvider, useTab } from 'contexts/TabContext';
import { TabAreaContainer, TabList, Tab, TabLabel, TabContent } from 'styles/common.styles';

/**
 * PageInnerTabs
 *
 * 목적:
 * - Layout의 전역 탭(TabArea)과 별개로, "페이지 내부"에서 고정 탭을 구성하고 싶을 때 사용
 * - 기존 TabProvider 로직(openTab/activateTab)을 그대로 재사용
 *
 */
const InnerTabsBody = ({ tabsConfig, defaultActiveTabId, sharedProps, keepMounted }) => {
  const { tabs, activeTabId, openTab, activateTab } = useTab();
  const initializedRef = useRef(false);
  const [activeChangeSeq, setActiveChangeSeq] = useState(0);

  useEffect(() => {
    // 고정 탭이므로 최초 1회만 등록 (활성 탭은 마지막에 한 번만 지정)
    if (initializedRef.current) return;
    initializedRef.current = true;

    (tabsConfig ?? []).forEach((t) => {
      openTab(
        {
          id: t.id,
          label: t.label,
          component: t.component,
        },
        { activate: false }
      );
    });

    const initialTabId = defaultActiveTabId || tabsConfig?.[0]?.id;
    if (initialTabId) {
      setTimeout(() => {
        activateTab(initialTabId);
        setActiveChangeSeq(1);
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onActivateTab = useCallback((tabId) => {
    activateTab(tabId);
    setActiveChangeSeq((prev) => prev + 1);
  }, [activateTab]);

  const ActiveComponent = useMemo(() => {
    return tabs.find((t) => t.id === activeTabId)?.component ?? null;
  }, [activeTabId, tabs]);

  const TabPanel = useCallback(({ tab, isActive }) => {
    const TabComponent = tab?.component ?? null;
    if (!TabComponent) return null;

    return (
      <div
        key={tab.id}
        style={{
          display: isActive ? 'flex' : 'none',
          width: '100%',
          height: '100%',
          minHeight: 0,
          flexDirection: 'column',
        }}
      >
        {/* Suspense : 탭 컴포넌트가 로딩되는 동안 로딩 표시 */}
        {/* fallback : 탭 컴포넌트가 로딩되는 동안 로딩 표시 */}
        {/* TabComponent : 탭 컴포넌트 */}
        <Suspense fallback={<div style={{ padding: 12 }}>로딩중...</div>}>
          <TabComponent
            {...(sharedProps || {})}
            tabId={tab.id}
            tabLabel={tab.label}
            isActive={isActive}
            activeTabId={activeTabId}
            activeChangeSeq={activeChangeSeq} 
          />
        </Suspense>
      </div>
    );
  }, [activeChangeSeq, activeTabId, sharedProps]);

  return (
    <TabAreaContainer>
      {/* 전역 TabArea와 동일한 디자인(닫기 버튼만 제외) */}
      <TabList>
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            type="button"
            $active={tab.id === activeTabId}
            onClick={() => onActivateTab(tab.id)}
          >
            <TabLabel>{tab.label}</TabLabel>
          </Tab>
        ))}
      </TabList>

      <TabContent>
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {keepMounted
            ? tabs.map((tab) => (
              <TabPanel
                key={tab.id}
                tab={tab}
                isActive={tab.id === activeTabId}
              />
            ))
            : (
              <Suspense fallback={<div style={{ padding: 12 }}>로딩중...</div>}>
                {ActiveComponent ? (
                  <ActiveComponent
                    {...(sharedProps || {})}
                    tabId={activeTabId}
                    isActive
                    activeTabId={activeTabId}
                    activeChangeSeq={activeChangeSeq}
                  />
                ) : null}
              </Suspense>
            )}
        </div>
      </TabContent>
    </TabAreaContainer>
  );
};

/**
 * @param {Object} props
 * @param {{id:string,label:string,component:any}[]} props.tabsConfig - 고정 탭 구성
 * @param {string} props.defaultActiveTabId - 초기 활성 탭 id
 * @param {Object} props.sharedProps - 부모에서 탭으로 전달할 공통 props
 * @param {boolean} props.keepMounted - 비활성 탭도 언마운트하지 않고 유지 (active change 감지용)
 */
const PageInnerTabs = ({ tabsConfig = [], defaultActiveTabId, sharedProps, keepMounted = true }) => {
  return (
    <TabProvider>
      <InnerTabsBody
        tabsConfig={tabsConfig}
        defaultActiveTabId={defaultActiveTabId}
        sharedProps={sharedProps}
        keepMounted={keepMounted}
      />
    </TabProvider>
  );
};

export default PageInnerTabs;

